import axios, { AxiosError, type AxiosInstance } from "axios";

import { trackRestFreshness } from "@/lib/rest-freshness";
import { useAuthStore } from "@/store/auth";

export const api: AxiosInstance = axios.create({
  baseURL: "/api/v1",
  timeout: 10_000,
  withCredentials: true,  // CSRF cookie 자동 포함
});


/** CSRF cookie 읽기 (double-submit 헤더로 보내기 위해). */
function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

const CSRF_COOKIE = "argos_csrf";
const MUTATING = new Set(["post", "put", "patch", "delete"]);

// 요청 인터셉터 — JWT + CSRF
api.interceptors.request.use(async (config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // CSRF — mutating method 시 cookie → header 복제 (double-submit)
  const method = (config.method ?? "get").toLowerCase();
  if (MUTATING.has(method)) {
    let csrf = readCookie(CSRF_COOKIE);
    if (!csrf) {
      // cookie 없으면 한 번 발급 받기 (auth/login·register·refresh 는 면제이지만 안전)
      try {
        const r = await axios.get("/api/v1/auth/csrf-token", { withCredentials: true });
        csrf = r.data?.csrf_token ?? readCookie(CSRF_COOKIE);
      } catch {
        // CSRF endpoint 실패 시 무시 (test 환경 등에서 OFF 가능)
      }
    }
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// 응답 인터셉터 — SW NetworkFirst 캐시 폴백(오프라인 스냅샷) 감지 (지침 §7-3).
// 캐시 폴백은 HTTP 200 으로 성공처럼 보이므로 Date 헤더 나이로 판별해
// connection 스토어에 보고한다 (ConnectionBanner "오프라인 스냅샷 표시 중").
api.interceptors.response.use((r) => {
  trackRestFreshness(r);
  return r;
});

// 응답 인터셉터 — 401 시 refresh 시도
let refreshing: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;
  try {
    const r = await axios.post("/api/v1/auth/refresh", { refresh_token: refresh });
    const access = r.data.access_token as string;
    const newRefresh = r.data.refresh_token as string;
    useAuthStore.getState().setTokens(access, newRefresh);
    return access;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as (typeof err.config & { _retry?: boolean });
    if (err.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= tryRefresh();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers!.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
    }
    return Promise.reject(err);
  },
);
